/**
 * Lightweight CoinGecko price cache.
 *
 * - Fetches prices at most once every CACHE_TTL_MS (5 min).
 * - On HTTP 429 (rate-limited) falls back silently to the last good prices.
 * - Exposes pure-math helpers: usdToCoin / coinToUsd — no external library needed.
 */

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const COINGECKO_IDS = {
  BTC: "bitcoin",
  ETH: "ethereum",
  BNB: "binancecoin",
  TRX: "tron",
  USDT: "tether",
  SOL: "solana",
  ARB: "arbitrum",
  AVAX: "avalanche-2",
  POLY: "matic-network",
};

const COINGECKO_URL =
  "https://api.coingecko.com/api/v3/simple/price" +
  "?ids=bitcoin%2Cethereum%2Cbinancecoin%2Ctron%2Ctether%2Csolana%2Carbitrum%2Cavalanche-2%2Cmatic-network" +
  "&vs_currencies=usd&include_24hr_change=true";

// --- internal state ---
let cachedPrices = null; // { BTC: { usd, usd_24h_change }, ETH: {...}, ... }
let lastFetchedAt = 0;
let inflight = null; // Promise while a fetch is in progress

function normalizeTicker(ticker) {
  const t = String(ticker || "").toUpperCase();
  if (t.startsWith("USDT")) return "USDT";
  if (t === "MATIC" || t === "POLYGON") return "POLY";
  return t;
}

/**
 * Map raw CoinGecko response to { TICKER: { usd, usd_24h_change } }.
 */
function mapResponse(data) {
  const result = {};
  for (const [ticker, geckoId] of Object.entries(COINGECKO_IDS)) {
    const entry = data[geckoId];
    if (entry) {
      result[ticker] = {
        usd: entry.usd ?? 1,
        usd_24h_change: entry.usd_24h_change ?? 0,
      };
    }
  }
  return result;
}

/**
 * Returns cached (or freshly fetched) prices.
 * Always resolves — on 429 or error it resolves with the last good cache.
 */
export async function fetchPrices() {
  const now = Date.now();

  if (cachedPrices && now - lastFetchedAt < CACHE_TTL_MS) {
    return { prices: cachedPrices, raw: null, fromCache: true };
  }

  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await fetch(COINGECKO_URL);
      if (res.status === 429) {
        console.warn("[priceCache] CoinGecko 429 — using stale prices");
        return {
          prices: cachedPrices,
          raw: null,
          fromCache: true,
          rateLimited: true,
        };
      }
      if (!res.ok) throw new Error(`CoinGecko HTTP ${res.status}`);
      const data = await res.json();
      cachedPrices = mapResponse(data);
      lastFetchedAt = Date.now();
      return { prices: cachedPrices, raw: data, fromCache: false };
    } catch (err) {
      console.error("[priceCache] fetch error:", err);
      return { prices: cachedPrices, raw: null, fromCache: true, error: err };
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** Synchronously return in-memory cached prices (may be null before first fetch). */
export function getCachedPrices() {
  return cachedPrices;
}

/**
 * Convert a USD amount to the equivalent coin amount.
 * @param {number} usdAmount
 * @param {string} ticker  e.g. "BTC", "ETH", "USDT TRC20"
 * @returns {number}
 */
export function usdToCoin(usdAmount, ticker) {
  if (!cachedPrices) return 0;
  const key = normalizeTicker(ticker);
  if (key === "USDT") return Number(usdAmount);
  const entry = cachedPrices[key];
  if (!entry || !entry.usd) return 0;
  return Number(usdAmount) / entry.usd;
}

/**
 * Convert a coin amount to USD.
 * @param {number} coinAmount
 * @param {string} ticker  e.g. "BTC"
 * @returns {number}
 */
export function coinToUsd(coinAmount, ticker) {
  if (!cachedPrices) return 0;
  const key = normalizeTicker(ticker);
  if (key === "USDT") return Number(coinAmount);
  const entry = cachedPrices[key];
  if (!entry || !entry.usd) return 0;
  return Number(coinAmount) * entry.usd;
}

/**
 * Get the current USD price for a ticker.
 * @param {string} ticker
 * @returns {number}
 */
export function getPrice(ticker) {
  if (!cachedPrices) return 0;
  const key = normalizeTicker(ticker);
  if (key === "USDT") return cachedPrices.USDT?.usd ?? 1;
  return cachedPrices[key]?.usd ?? 0;
}

/**
 * Get the 24h change percentage for a ticker.
 * @param {string} ticker
 * @returns {number}
 */
export function get24hChange(ticker) {
  if (!cachedPrices) return 0;
  return cachedPrices[normalizeTicker(ticker)]?.usd_24h_change ?? 0;
}
